import { useState, useCallback } from 'react';
import { Cpu, MessageCircle, Zap } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { TransitDealChatbot } from '../chatbot/TransitDealChatbot';
import { createApiClient } from '../services/api';
import type { ChatbotMessageResponse } from '../types';

export function ChatbotPage() {
  const { getToken, account } = useAppAuth();
  const [sessionId, setSessionId] = useState<string | undefined>();

  const apiClient = createApiClient(getToken);

  const sendMessage = useCallback(
    async (message: string, sid?: string): Promise<ChatbotMessageResponse> => {
      const response = await apiClient.post<ChatbotMessageResponse>('/api/chatbot/message', {
        message,
        sessionId: sid ?? sessionId,
        userId: account?.userId,
      });
      if (!sessionId && response.data.sessionId)
        setSessionId(response.data.sessionId);
      return response.data;
    },
    [apiClient, sessionId, account]
  );

  const firstName = account?.name?.split(' ')[0] ?? 'there';
  const greeting  = !!account
    ? `Hi ${firstName}! I can help you post a job, complete your driver profile, or answer any platform questions.`
    : "Hi! I'm the TransitDeal assistant. I can help you post a delivery job or learn about the platform.";

  return (
    <div className="page-shell">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-2xl mb-4">
          <Cpu className="w-7 h-7 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">TransitDeal Assistant</h1>
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          Post a delivery job by chatting, build your driver profile step-by-step,
          or get answers about fees, relay routes, and payments.
        </p>
      </div>

      {/* Capability chips */}
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {[
          { icon: <Zap className="w-3 h-3" />,           label: 'Post a job by chatting' },
          { icon: <MessageCircle className="w-3 h-3" />, label: 'Build driver profile step-by-step' },
          { icon: <MessageCircle className="w-3 h-3" />, label: 'Explain fees & relay model' },
          { icon: <MessageCircle className="w-3 h-3" />, label: 'Help with disputes & POD' },
        ].map(chip => (
          <span key={chip.label}
            className="inline-flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700
                       border border-blue-100 px-3 py-1.5 rounded-full">
            {chip.icon}{chip.label}
          </span>
        ))}
      </div>

      {/* Chatbot widget */}
      <div className="shadow-lg rounded-2xl overflow-hidden border border-gray-200">
        <TransitDealChatbot
          sendMessage={sendMessage}
          userId={account?.userId}
          initialGreeting={greeting}
        />
      </div>

      <p className="text-center text-xs text-gray-400 mt-4">
        Powered by Azure OpenAI · Conversations are not stored permanently
      </p>
    </div>
  );
}
